import type { ThemeOptions } from '@mui/material/styles';
import { controlHeights, iconSizes, scaledRem, spacingUnit } from './tokens';

export const FontWeights = {
    Regular: 300,
    Medium: 500,
    SemiBold: 600,
    Bold: 700,
};

const defaultTheme: ThemeOptions = {
    spacing: spacingUnit,
    palette: {
        // primary: {
        //     main: '#020d1f',
        //     contrastText: '#fff',
        // },
        custom: { contentBgColor: '#f4f5fa' },
        secondary: {
            // main: '#53565a',
            main: 'rgb(117, 117, 117)',
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        tertiary: {
            main: '#14b8a6',
        },
        quaternary: {
            main: '#f59e0b',
        },
        quinary: {
            main: '#ef4444',
        },
        senary: {
            main: '#8b5cf6',
        },
        // error: {
        //     main: '#FE2F7C',
        //     light: '#FFE9F3',
        // },
        success: {
            main: '#13A454',
        },
        text: {
            primary: '#2c2a29',
            // primary: '#787e9d',
        },
    },
    typography: {
        fontFamily: 'Rubik, sans-serif',
        h1: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(2.4),
            lineHeight: '1em',
        },
        h2: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(2.2),
            lineHeight: '1.2571em',
        },
        h3: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(1.8),
            lineHeight: '1.42em',
        },
        h4: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(1.4),
            lineHeight: '1.3em',
        },
        h5: {
            fontWeight: FontWeights.SemiBold,
            fontSize: scaledRem(1.3),
            lineHeight: '1.25em',
        },
        h6: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(1.1),
            lineHeight: '1.3889em',
        },
        subtitle1: {
            fontWeight: FontWeights.Regular,
            fontSize: scaledRem(1.2),
            lineHeight: '1.7em',
            letterSpacing: '0.0015em',
        },
        subtitle2: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(0.9),
            lineHeight: '1.7142em',
        },
        body1: {
            fontWeight: FontWeights.Regular,
            fontSize: scaledRem(1.1),
            lineHeight: '1.625em',
        },
        body2: {
            fontWeight: FontWeights.Regular,
            fontSize: scaledRem(1),
            lineHeight: '1.7142em',
        },
        button: {
            fontWeight: FontWeights.Regular,
            textTransform: 'none',
            fontSize: scaledRem(0.95),
        },
        caption: {
            fontWeight: FontWeights.Regular,
            fontSize: scaledRem(0.75),
            lineHeight: '1.3333em',
        },
        overline: {
            fontWeight: FontWeights.Medium,
            fontSize: scaledRem(0.65),
            lineHeight: '1.6em',
            textTransform: 'uppercase',
        },
    },
    mixins: {
        toolbar: {
            minHeight: spacingUnit * 8,
        },
    },
    components: {
        MuiToolbar: {
            styleOverrides: {
                regular: {
                    minHeight: spacingUnit * 8,
                },
                dense: {
                    minHeight: spacingUnit * 6,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                root: {
                    minHeight: controlHeights.md * 0.8,
                    borderRadius: scaledRem(2),
                    paddingTop: spacingUnit * 0.4,
                    paddingBottom: spacingUnit * 0.4,
                    paddingLeft: spacingUnit * 1.6,
                    paddingRight: spacingUnit * 1.6,
                },
            },
        },
        MuiIconButton: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                root: {
                    padding: spacingUnit,
                    '& .MuiSvgIcon-root': {
                        fontSize: iconSizes.md * 1.2,
                    },
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                root: {
                    padding: spacingUnit * 0.75,
                    '& .MuiSvgIcon-root': {
                        fontSize: iconSizes.md * 1.2,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: controlHeights.md,
                    paddingLeft: spacingUnit * 2,
                    paddingRight: spacingUnit * 2,
                    fontSize: scaledRem(0.9),
                },
            },
        },
        MuiListItem: {
            defaultProps: {
                dense: true,
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    minHeight: controlHeights.md,
                    paddingTop: spacingUnit * 0.75,
                    paddingBottom: spacingUnit * 0.75,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: spacingUnit * 4,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
        },
        MuiFormControl: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiSelect: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    minHeight: controlHeights.md,
                },
                input: {
                    fontSize: scaledRem(0.95),
                    paddingTop: spacingUnit,
                    paddingBottom: spacingUnit,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    margin: spacingUnit * 2,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit * 2}px ${spacingUnit * 2.5}px`,
                    fontSize: scaledRem(1.05),
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit * 1.5}px ${spacingUnit * 2.5}px`,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit * 1.25}px ${spacingUnit * 2.5}px`,
                    gap: spacingUnit,
                },
            },
        },
        MuiChip: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                root: {
                    fontSize: scaledRem(0.8),
                    height: controlHeights.sm,
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit * 1.5}px ${spacingUnit * 2}px`,
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit * 1.5}px`,
                    '&:last-child': {
                        paddingBottom: spacingUnit * 1.5,
                    },
                },
            },
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit}px ${spacingUnit * 1.5}px`,
                },
            },
        },
        MuiMenuItem: {
            defaultProps: {
                dense: true,
            },
            styleOverrides: {
                root: {
                    minHeight: controlHeights.md,
                    fontSize: scaledRem(0.9),
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    paddingTop: spacingUnit * 0.5,
                    paddingBottom: spacingUnit * 0.5,
                    alignItems: 'center',
                },
                icon: {
                    paddingTop: spacingUnit * 0.25,
                    paddingBottom: spacingUnit * 0.25,
                    marginRight: spacingUnit,
                },
                message: {
                    paddingTop: spacingUnit * 0.25,
                    paddingBottom: spacingUnit * 0.25,
                },
                action: {
                    marginRight: 0,
                    paddingTop: spacingUnit * 0.25,
                    paddingBottom: spacingUnit * 0.25,
                },
            },
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    marginBottom: spacingUnit * 0.5,
                    lineHeight: 1.2,
                },
            },
        },
        MuiTable: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: `${spacingUnit}px ${spacingUnit * 1.5}px`,
                    fontSize: scaledRem(0.9),
                },
                sizeSmall: {
                    padding: `${spacingUnit * 0.75}px ${spacingUnit * 1.25}px`,
                },
            },
        },
        // MuiPaper: {
        //     defaultProps: {
        //         // elevation: 0,
        //         // sx: { borderRadius: 1 },
        //     },
        // },
        // MuiAlert: {
        //     defaultProps: {
        //         sx: { borderWidth: 2, borderColor: 'warning.light', borderStyle: 'solid' },
        //     },
        // },
        // this will literally happen on all tables everywhere, so need to think of different solution
        // MuiTableCell: {
        //     styleOverrides: {
        //         stickyHeader: state => ({
        //             backgroundColor: state.theme.palette.custom.contentBgColor,
        //         }),
        //     },
        // },
    },
};

export default defaultTheme;
